'use client'

import { useMemo } from 'react'
import { getRoomShareTemplate } from '@/lib/services/email/templates/room-share-template'
import { getShareContentTemplate } from '@/lib/services/email/templates/share-content-template'
import type { ContactOrUser } from '@/components/share-room/types'
import { buildSharedRoomUrl } from '@/lib/shared-room/build-shared-room-url'

export type ShareRoomPreviewSessionUser = {
  firstName?: string | null
  lastName?: string | null
  name?: string | null
}

/**
 * Same HTML as sent mail (`getRoomShareTemplate` / `getShareContentTemplate` + base template).
 */
export function useShareRoomEmailPreview(options: {
  roomName: string
  message: string
  contentId?: string
  contentName?: string
  sessionUser?: ShareRoomPreviewSessionUser | null
  selectedContactsAndUsers: ContactOrUser[]
}) {
  const {
    roomName,
    message,
    contentId,
    contentName,
    sessionUser,
    selectedContactsAndUsers,
  } = options

  const html = useMemo(() => {
    const inviterName =
      sessionUser?.firstName && sessionUser?.lastName
        ? `${sessionUser.firstName} ${sessionUser.lastName}`
        : sessionUser?.name || sessionUser?.firstName || 'You'
    const mockRecipientName =
      selectedContactsAndUsers.length === 1
        ? selectedContactsAndUsers[0].firstName
        : 'John'
    const previewOrigin =
      typeof window !== 'undefined'
        ? window.location.origin
        : (process.env.NEXT_PUBLIC_APP_URL ?? 'https://www.smartroomsxp.com')
    const shareableUrl = buildSharedRoomUrl(previewOrigin, 'preview', contentId)

    if (contentName) {
      return getShareContentTemplate({
        contentName,
        roomName,
        customMessage: message.trim() || undefined,
        shareableUrl,
        inviterName,
        recipientName: mockRecipientName,
      })
    }

    return getRoomShareTemplate({
      roomName,
      customMessage: message.trim() || undefined,
      shareableUrl,
      inviterName,
      recipientName: mockRecipientName,
      hidePromotionalSection: true,
    })
  }, [
    sessionUser?.firstName,
    sessionUser?.lastName,
    sessionUser?.name,
    selectedContactsAndUsers,
    roomName,
    message,
    contentId,
    contentName,
  ])

  return { html }
}
